import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Target, Zap, Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';

export const CommitmentSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const commitmentLevels = [
    {
      icon: Target,
      sessions: '1 Session/Week',
      level: 'Tier 1',
      description: 'Perfect for beginners or families with busy schedules. Get your child started on their football journey without overcommitting.',
      memberRate: '$42.30/session',
      nonMemberRate: '$47.00/session',
      ideal: 'Beginners & Busy Families'
    },
    {
      icon: Zap,
      sessions: '2 Sessions/Week',
      level: 'Tier 2',
      description: 'Ideal balance between football development and other activities. Recommended for players wanting steady improvement.',
      memberRate: '$37.65/session',
      nonMemberRate: '$41.83/session',
      savings: '11%',
      ideal: 'Steady Development'
    },
    {
      icon: Trophy,
      sessions: '3 Sessions/Week',
      level: 'Tier 3',
      description: 'Maximum development for dedicated players serious about football. Includes training and competitive match opportunities.',
      memberRate: '$32.00/session',
      nonMemberRate: '$35.56/session',
      savings: '24%',
      ideal: 'Serious Players',
      highlighted: true
    },
  ];

  return (
    <section id="commitment" className="min-h-screen py-24 px-6 bg-charcoal">
      <div className="max-w-6xl mx-auto">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-12">
            <Badge variant="outline" className="gold-border text-primary mb-4">
              Flexible Options
            </Badge>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gradient-gold mb-4">
              Select Your Commitment Level
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base">
              SCC provides options to suit your family&apos;s budget and schedule. 
              Choose the commitment level that works best for you.
            </p>
          </motion.div>

          {/* Commitment Cards */}
          <motion.div variants={itemVariants} className="grid md:grid-cols-3 gap-6 mb-12">
            {commitmentLevels.map((level, idx) => {
              const Icon = level.icon;
              return (
                <motion.div
                  key={idx}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card className={`bg-gradient-card gold-border h-full flex flex-col ${
                    level.highlighted ? 'ring-2 ring-primary shadow-glow' : ''
                  }`}>
                    <CardHeader className="pb-2">
                      {level.highlighted && (
                        <Badge className="bg-primary text-primary-foreground w-fit mb-2">
                          Best Value
                        </Badge>
                      )}
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`p-2 rounded-lg ${level.highlighted ? 'bg-primary/20' : 'bg-charcoal'}`}>
                          <Icon className={`h-5 w-5 ${level.highlighted ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <CardTitle className="font-display text-lg text-card-foreground">
                            {level.sessions}
                          </CardTitle>
                          <CardDescription className="text-primary font-medium">
                            {level.level}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-2 flex flex-col flex-grow">
                      <p className="text-muted-foreground text-sm mb-4 flex-grow">
                        {level.description}
                      </p>
                      
                      <div className="space-y-2 pt-4 border-t border-border">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground text-sm">Member Rate:</span>
                          <span className="text-primary font-bold">{level.memberRate}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground text-sm">Non-Member:</span>
                          <span className="text-card-foreground font-medium">{level.nonMemberRate}</span>
                        </div>
                        {level.savings && (
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground text-sm">Savings:</span>
                            <Badge className="bg-success/20 text-success border-0">
                              {level.savings} off base rate
                            </Badge>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-4 pt-3 border-t border-border">
                        <p className="text-xs text-muted-foreground text-center">
                          Ideal for: <span className="text-primary">{level.ideal}</span>
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Bottom Message */}
          <motion.div variants={itemVariants}>
            <Card className="bg-primary/10 border-primary/30">
              <CardContent className="p-6 text-center">
                <h4 className="font-display text-lg text-primary mb-2">
                  Your Choice, Your Budget
                </h4>
                <p className="text-muted-foreground text-sm max-w-2xl mx-auto">
                  We understand every family has different priorities and budgets. 
                  SCC empowers you to choose the commitment level that fits your lifestyle. 
                  Whether it&apos;s 1 session for a gentle introduction or 3 sessions for serious development, 
                  the decision is yours to make based on what works best for your family.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default CommitmentSection;
